// #283 — `setFormValue` used to validate on every keystroke, so filling the
// public application form fired ~178 `POST /<collection>/validate`. That trips
// the public-form rate limit (#214, tier A: burst 40 then 1 req/s); the
// resulting 429s carry no `errors` body, so `setErrors` (which only writes when
// a payload is present) silently kept the errors from the last request that got
// through — taken when the form was still nearly empty. Filled fields stayed
// marked "required" and the form never submitted. Debouncing collapses a burst
// of typing into a single request.
//
// #291 — the queue is module-level rather than per-store on purpose. A single
// form composes several collections (Members/Single.vue sums the errors of
// members + contacts + memberStates) and every generateStore() call closes over
// its own state, so a per-store flush makes each submit handler responsible for
// naming every store it happens to touch. One no-argument flush settles all of
// them.
export const VALIDATE_DEBOUNCE_MS = 400

// id -> { timer, run }. Ids are `${store}:${listName}:${key}`.
const scheduled = new Map()
// Validates already sent but not yet settled. A debounce that fired just before
// the user clicked leaves no timer to flush, yet `errorCount` is still about to
// change — flushing has to wait for these too.
const inFlight = new Set()

// Swallow the outcome: a failed validate reports itself through the store's
// error state, and flushPendingValidates() must settle whatever happens.
function track(promise) {
  const settled = promise.then(
    () => {},
    () => {},
  )
  inFlight.add(settled)
  settled.then(() => {
    inFlight.delete(settled)
  })
  return settled
}

export function scheduleValidate(id, run) {
  cancelScheduledValidate(id)
  const timer = setTimeout(() => {
    scheduled.delete(id)
    track(run())
  }, VALIDATE_DEBOUNCE_MS)
  scheduled.set(id, { timer, run })
}

export function cancelScheduledValidate(id) {
  const entry = scheduled.get(id)
  if (typeof entry === 'undefined') {
    return
  }
  clearTimeout(entry.timer)
  scheduled.delete(id)
}

// Drop every validate scheduled for a list. Without this a cleared list leaves a
// timer that fires against a record that no longer exists.
export function cancelScheduledValidatesFor(prefix) {
  Array.from(scheduled.keys()).forEach((id) => {
    if (id.startsWith(prefix)) {
      cancelScheduledValidate(id)
    }
  })
}

// Submit handlers must await this before reading `errorCount`: with the debounce
// in place the accumulated errors lag the last keystroke, so typing the last
// required field and clicking Guardar within the debounce window reads the
// errors from before that keystroke and refuses a form that is actually valid
// (#291).
//
// It takes no arguments by design. The queue is keyed by store/list/key, and a
// hand-passed key that misses nothing fails silently — which is exactly the
// mistake this issue is about.
//
// It never rejects. A validate that fails (429, network) leaves `errorCount`
// stale, so the guard falls through to `save`, which revalidates server-side and
// returns the real errors. Blocking on errors we know to be stale is #283.
export function flushPendingValidates() {
  const flushed = []
  scheduled.forEach((entry, id) => {
    clearTimeout(entry.timer)
    scheduled.delete(id)
    flushed.push(track(entry.run()))
  })
  return Promise.all(Array.from(inFlight).concat(flushed))
}
